import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Copy, Check, ShieldCheck, CheckCircle2, Send, PackageSearch, Tag, X, Loader2 } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { siteConfig as fallback } from "../mock";
import { useCart } from "../context/CartContext";
import { useSite } from "../context/SiteContext";
import { createOrder, img, validatePromo } from "../lib/api";
import { toast } from "../hooks/use-toast";

const Checkout = () => {
  const { items, total: subtotal, clearCart } = useCart();
  const { settings } = useSite();
  const s = settings || fallback;
  const navigate = useNavigate();
  const payments = s.payments || [];
  const shippingMethods = s.shippingMethods || [];

  const [payment, setPayment] = useState(payments[0]?.id);
  const [shipId, setShipId] = useState(shippingMethods[0]?.id);
  const [copied, setCopied] = useState("");
  const [placed, setPlaced] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [orderId, setOrderId] = useState("");
  const [form, setForm] = useState({ name: "", email: "", address: "", city: "", zip: "", country: "" });
  const [codeInput, setCodeInput] = useState("");
  const [promo, setPromo] = useState(null);
  const [promoBusy, setPromoBusy] = useState(false);
  const [finalTotal, setFinalTotal] = useState(0);

  useEffect(() => { window.scrollTo(0, 0); }, []);
  useEffect(() => { if (!payment && payments[0]) setPayment(payments[0].id); if (!shipId && shippingMethods[0]) setShipId(shippingMethods[0].id); }, [payments, shippingMethods]);

  const selectedPayment = payments.find((p) => p.id === payment);
  const selectedShip = shippingMethods.find((m) => m.id === shipId);
  const shipPrice = selectedShip?.price || 0;
  const discount = promo ? (promo.type === "percent" ? Math.round(subtotal * promo.value) / 100 : Math.min(promo.value, subtotal)) : 0;
  const grandTotal = Math.max(0, subtotal + shipPrice - discount);

  const applyPromo = async () => {
    if (!codeInput.trim()) return;
    setPromoBusy(true);
    try { const r = await validatePromo(codeInput.trim(), subtotal); setPromo(r); toast({ title: "Promo applied", description: `${r.code} saved you $${r.discount.toFixed(2)}` }); }
    catch (e) { setPromo(null); toast({ title: "Promo code", description: e?.response?.data?.detail || "Invalid code" }); }
    finally { setPromoBusy(false); }
  };

  const copyAddress = (addr) => { navigator.clipboard.writeText(addr); setCopied(addr); setTimeout(() => setCopied(""), 1800); };

  const placeOrder = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.address) {
      toast({ title: "Missing details", description: "Please fill in your shipping information." });
      return;
    }
    setPlacing(true);
    try {
      const payload = {
        customer: form,
        items: items.map((i) => ({ id: i.id, name: i.name, option: i.option, price: i.price, qty: i.qty, image: i.image })),
        subtotal,
        total: grandTotal,
        paymentMethod: selectedPayment?.name || payment,
        shippingMethod: selectedShip ? { name: selectedShip.name, price: selectedShip.price } : null,
        promoCode: promo?.code || "",
      };
      const res = await createOrder(payload);
      setOrderId(res.orderId);
      setFinalTotal(res.total);
      setPlaced(true);
      clearCart();
    } catch (err) {
      toast({ title: "Something went wrong", description: err?.response?.data?.detail || "Could not place your order. Please try again." });
    } finally {
      setPlacing(false);
    }
  };

  if (placed) {
    return (
      <div className="bg-navy min-h-screen flex items-center justify-center px-4 py-20">
        <div className="pop-in max-w-lg w-full glass-gold-card rounded-3xl p-10 text-center" data-testid="order-success">
          <div className="h-16 w-16 rounded-full bg-emerald-500/15 border border-emerald-400/40 flex items-center justify-center mx-auto mb-6"><CheckCircle2 size={34} className="text-emerald-400" /></div>
          <h2 className="text-2xl font-bold text-white">Order Received!</h2>
          <p className="text-slate-400 mt-3">Your order <span className="text-gold font-semibold" data-testid="order-success-id">{orderId}</span> totalling <span className="text-gold font-semibold">${Number(finalTotal).toFixed(2)}</span> has been placed. A confirmation email is on its way. Send your payment and message us on Telegram with your order ID to confirm. Track your shipment anytime below.</p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
            <button onClick={() => navigate(`/track?order=${orderId}`)} data-testid="order-success-track-btn" className="inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-light text-navy font-semibold px-6 py-3 rounded-xl transition-colors"><PackageSearch size={17} /> Track Order</button>
            {s.contact?.telegramLink && <a href={s.contact?.telegramLink} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 border border-gold/30 hover:border-gold text-white px-6 py-3 rounded-xl transition-colors"><Send size={17} /> Confirm on Telegram</a>}
          </div>
          <Link to="/" className="mt-5 inline-block text-slate-400 hover:text-gold transition-colors text-sm">Back to home</Link>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="bg-navy min-h-screen flex flex-col items-center justify-center text-center px-4">
        <h2 className="text-2xl font-bold text-white mb-4">Your cart is empty</h2>
        <Link to="/" className="inline-flex items-center gap-2 bg-gold text-navy font-semibold px-6 py-3 rounded-xl">Continue Shopping</Link>
      </div>
    );
  }

  const inputCls = "w-full bg-navy-700 border border-white/15 focus:border-gold rounded-xl px-4 py-3 text-white placeholder-slate-500 outline-none transition-colors";

  return (
    <div className="bg-navy min-h-screen py-10">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-slate-300 hover:text-gold transition-colors mb-8"><ArrowLeft size={18} /> Back</button>
        <h1 className="text-3xl md:text-4xl font-bold text-white mb-10">Checkout</h1>
        <div className="grid lg:grid-cols-5 gap-10">
          <form onSubmit={placeOrder} className="lg:col-span-3 space-y-8">
            <div className="bg-navy-800 rounded-2xl border border-white/10 p-7">
              <h3 className="text-white font-bold text-lg mb-5">1. Shipping Information</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <input className={inputCls} data-testid="checkout-name-input" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                <input className={inputCls} data-testid="checkout-email-input" placeholder="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                <input className={`${inputCls} sm:col-span-2`} data-testid="checkout-address-input" placeholder="Street address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
                <input className={inputCls} data-testid="checkout-city-input" placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
                <input className={inputCls} data-testid="checkout-zip-input" placeholder="ZIP / Postal code" value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} />
                <input className={`${inputCls} sm:col-span-2`} data-testid="checkout-country-input" placeholder="Country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} />
              </div>
            </div>

            <div className="bg-navy-800 rounded-2xl border border-white/10 p-7">
              <h3 className="text-white font-bold text-lg mb-5">2. Shipping Method</h3>
              <div className="space-y-3">
                {shippingMethods.map((m) => (
                  <button type="button" key={m.id} data-testid={`ship-option-${m.id}`} onClick={() => setShipId(m.id)} className={`w-full flex items-center justify-between text-left px-4 py-3.5 rounded-xl border transition-colors ${shipId === m.id ? "bg-gold/10 border-gold" : "border-white/15 hover:border-gold/40"}`}>
                    <div>
                      <p className="text-white font-medium">{m.name}</p>
                      <p className="text-slate-400 text-xs">{m.desc}</p>
                    </div>
                    <span className={`font-semibold ${m.price === 0 ? "text-emerald-400" : "text-gold"}`}>{m.price === 0 ? "FREE" : `$${m.price.toFixed(2)}`}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-navy-800 rounded-2xl border border-white/10 p-7">
              <h3 className="text-white font-bold text-lg mb-5">3. Payment Method</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                {payments.map((p) => (
                  <button type="button" key={p.id} data-testid={`pay-option-${p.id}`} onClick={() => setPayment(p.id)} className={`flex items-center gap-3 text-left px-4 py-3 rounded-xl border transition-colors ${payment === p.id ? "bg-gold/10 border-gold text-white" : "border-white/15 text-slate-300 hover:border-gold/40"}`}>
                    {p.logo && <img src={img(p.logo)} alt="" className="h-6 w-6" />}
                    <span className="font-medium">{p.name}</span>
                  </button>
                ))}
              </div>
              {selectedPayment && (
                <div className="mt-5 bg-navy-700 rounded-xl p-5 gold-border">
                  <div className="flex flex-col sm:flex-row gap-5 items-center">
                    <div className="bg-white p-3 rounded-xl shrink-0">
                      <QRCodeCanvas value={selectedPayment.address || ""} size={120} />
                    </div>
                    <div className="flex-1 w-full">
                      <p className="text-xs text-slate-400 mb-2">Scan the QR or send payment to this {selectedPayment.name} address:</p>
                      <div className="flex items-center gap-3">
                        <code className="flex-1 text-gold text-sm break-all">{selectedPayment.address}</code>
                        <button type="button" onClick={() => copyAddress(selectedPayment.address)} className="shrink-0 h-9 w-9 rounded-lg bg-navy-800 border border-gold/30 flex items-center justify-center text-gold hover:bg-navy transition-colors">{copied === selectedPayment.address ? <Check size={16} /> : <Copy size={16} />}</button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <button type="submit" disabled={placing} data-testid="checkout-place-order-btn" className="w-full btn-liquid text-navy font-bold py-4 rounded-xl disabled:opacity-60 disabled:cursor-not-allowed">
              <span className="relative z-10">{placing ? "Placing Order..." : `Place Order · $${grandTotal.toFixed(2)}`}</span>
            </button>
          </form>

          <div className="lg:col-span-2">
            <div className="bg-navy-800 rounded-2xl border border-white/10 p-7 sticky top-24">
              <h3 className="text-white font-bold text-lg mb-5">Order Summary</h3>
              <div className="space-y-4 max-h-64 overflow-y-auto pr-1">
                {items.map((i) => (
                  <div key={i.key} className="flex gap-3">
                    <img src={img(i.image)} alt={i.name} className="h-14 w-14 rounded-lg object-cover" />
                    <div className="flex-1 min-w-0"><p className="text-white text-sm font-medium truncate">{i.name}</p><p className="text-slate-400 text-xs">{i.option} &times; {i.qty}</p></div>
                    <span className="text-gold text-sm font-semibold">${(i.price * i.qty).toFixed(2)}</span>
                  </div>
                ))}
              </div>
              <div className="mt-5" data-testid="checkout-promo">
                {promo ? (
                  <div className="flex items-center justify-between bg-emerald-500/10 border border-emerald-400/30 rounded-xl px-4 py-3">
                    <span className="inline-flex items-center gap-2 text-emerald-300 text-sm font-semibold"><Tag size={15} /> {promo.code}</span>
                    <button type="button" onClick={() => { setPromo(null); setCodeInput(""); }} data-testid="checkout-promo-remove" className="text-slate-400 hover:text-red-400"><X size={16} /></button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input data-testid="checkout-promo-input" value={codeInput} onChange={(e) => setCodeInput(e.target.value.toUpperCase())} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), applyPromo())} placeholder="Promo code" className="flex-1 bg-navy-700 border border-white/15 focus:border-gold rounded-xl px-4 py-2.5 text-white text-sm placeholder-slate-500 outline-none transition-colors uppercase" />
                    <button type="button" onClick={applyPromo} disabled={promoBusy} data-testid="checkout-promo-apply" className="px-4 rounded-xl border border-gold/40 text-gold hover:bg-gold/10 text-sm font-semibold transition-colors disabled:opacity-60">{promoBusy ? <Loader2 size={15} className="animate-spin" /> : "Apply"}</button>
                  </div>
                )}
              </div>
              <div className="border-t border-white/10 mt-5 pt-5 space-y-2">
                <div className="flex justify-between text-slate-300 text-sm"><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
                <div className="flex justify-between text-slate-300 text-sm"><span>Shipping{selectedShip ? ` (${selectedShip.name})` : ""}</span><span className={shipPrice === 0 ? "text-emerald-400" : ""}>{shipPrice === 0 ? "FREE" : `$${shipPrice.toFixed(2)}`}</span></div>
                {discount > 0 && <div className="flex justify-between text-emerald-400 text-sm" data-testid="checkout-discount"><span>Discount ({promo.code})</span><span>-${discount.toFixed(2)}</span></div>}
                <div className="flex justify-between items-center pt-2"><span className="text-white font-semibold">Total</span><span className="text-gold font-display font-bold text-2xl" data-testid="checkout-total">${grandTotal.toFixed(2)}</span></div>
              </div>
              <div className="mt-5 flex items-center gap-2 text-xs text-slate-400"><ShieldCheck size={15} className="text-gold" /> Secure &amp; 100% discreet packaging</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
